'use client'

import React, { useEffect, useRef } from 'react'

import { createMarkup } from 'Services/functions'

type Props = {
  children?: string | null
  className?: string
}

/**
 * Drop-in replacement for `<Highlight innerHTML>` from `react-highlight`.
 *
 * Renders the same `<div>` with the same markup, and highlights the same
 * `pre code` nodes on mount — the difference is that highlight.js is fetched
 * lazily and only when the content actually contains a code block. As a static
 * import it put ~930 KB (raw) of language definitions into the first load of
 * every blog post — 15 of the 18 posts have no `<pre>` at all — and of /cto,
 * whose roadmap copy is prose.
 */
export default function HighlightedHtml({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current

    if (!el) return undefined

    const blocks = el.querySelectorAll<HTMLElement>('pre code')

    if (!blocks.length) return undefined

    let cancelled = false

    import('highlight.js').then(({ default: hljs }) => {
      if (cancelled) return

      blocks.forEach(block => hljs.highlightElement(block))
    })

    return () => {
      cancelled = true
    }
  }, [children])

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={createMarkup(children ?? '')}
      ref={ref}
    />
  )
}
